// OPERATOR-CONSOLE (docs/design/2026-10-30-fleet-tuning-loop.md §2.7, §2.8): the operator space.
// Super admins only (`cp.admit_super_admin`), and it works with zero organisations.
//
//   Settings: every setting set by hand before is a versioned row. Each apply is a new version
//   (scope platform | company | satellite), each version is kept, and rollback moves the pointer
//   back to an earlier one. The ClawPanel baseline is rendered from the rows; the roster is
//   generated with per-DE overrides.
//   Margin: our cost and the client price recorded on every run, by company and model.
//   Operator agents: Deployer, Fixer, Builder, each with a model setting (gateway, Anthropic API
//   key sealed in the keyring, or a subscription runner for operator work only), allowed APIs,
//   a daily cap, and job tokens scoped to named APIs.
//   Jobs: the Deployer's "New company from pack" and "Apply baseline", resumable checklists.
//
// Every write is a control-plane store and its `cp.platform_audit` row: no company event.
import { z } from "zod";
import { Currency, OperatorAgentSlug, OperatorAgentStatus, OperatorSettingKey } from "../enums";
import { BoundedJson, boundedRecord, Email, NonEmpty, Slug, Timestamp, Uuid } from "../primitives";
import { RenderedBaseline } from "./provisioning";
import { defineRoute } from "./route";

const op = "/v1/operator";
const operator = { feature: "core", tag: "operator", access: "operator" } as const;
// The runner's job-token routes: no session; the `X-Operator-Token: cosop_…` job token (or
// `Authorization: Bearer cosop_…`).
const runner = { feature: "core", tag: "operator", access: "public" } as const;

const Scope = z.enum(["platform", "company", "satellite"]);

// Settings ---------------------------------------------------------------------------------------

export const OperatorSettingParams = z.object({ key: OperatorSettingKey });
export const OperatorSettingQuery = z.object({
  company_id: Uuid.optional(),
  satellite_id: Uuid.optional(),
});

export const OperatorSettingVersionView = z
  .object({
    id: Uuid,
    version: z.int().min(1),
    /** Null: the override was cleared. */
    value: z.unknown().nullable(),
    note: z.string().nullable(),
    created_at: Timestamp,
    created_by_user_id: Uuid.nullable(),
    created_by_agent_id: Uuid.nullable(),
    /** The version the pointer is on now. */
    current: z.boolean(),
  })
  .meta({ id: "OperatorSettingVersionView" });

export const OperatorSettingDetail = z
  .object({
    key: OperatorSettingKey,
    /** The scopes this key takes. */
    scopes: z.array(Scope),
    /** Whether the console edits it here (false: it has its own page; history only). */
    editable: z.boolean(),
    scope: Scope,
    scope_id: Uuid.nullable(),
    /** The shipped default (null for a key kept in its own store). */
    default: z.unknown().nullable(),
    /** The stored platform value (null: none). */
    platform: z.unknown().nullable(),
    /** This scope's own value (company or satellite; null: none, or the platform scope). */
    override: z.unknown().nullable(),
    /** What applies at this scope. */
    effective: z.unknown().nullable(),
    source: z.enum(["satellite", "company", "platform", "default"]),
    /** This scope's version in force (null: nothing stored at this scope). */
    version: z.int().nullable(),
    /** This scope's versions, newest first (at most 50). */
    history: z.array(OperatorSettingVersionView),
  })
  .meta({ id: "OperatorSettingDetail" });
export type OperatorSettingDetail = z.infer<typeof OperatorSettingDetail>;

export const OperatorSettingSummary = z
  .object({
    key: OperatorSettingKey,
    scopes: z.array(Scope),
    editable: z.boolean(),
    /** The platform version in force (null: the shipped default). */
    version: z.int().nullable(),
    /** Company and satellite overrides stored. */
    overrides: z.int().min(0),
    updated_at: Timestamp.nullable(),
  })
  .meta({ id: "OperatorSettingSummary" });

export const OperatorSettingList = z
  .object({ settings: z.array(OperatorSettingSummary) })
  .meta({ id: "OperatorSettingList" });

/** Apply a new version (validated against the key's shape) or clear an override (`null`). */
export const OperatorSettingPut = z
  .strictObject({
    company_id: Uuid.nullable().optional(),
    satellite_id: Uuid.nullable().optional(),
    value: BoundedJson.nullable(),
    note: z.string().trim().max(500).optional(),
  })
  .meta({ id: "OperatorSettingPut" });

/** Move the pointer back to an earlier version of the same scope. */
export const OperatorSettingRollback = z
  .strictObject({
    company_id: Uuid.nullable().optional(),
    satellite_id: Uuid.nullable().optional(),
    version: z.int().min(1),
  })
  .meta({ id: "OperatorSettingRollback" });

// The rendered baseline, the roster ---------------------------------------------------------------

export const OperatorBaselineQuery = z.object({
  satellite_id: Uuid.optional(),
  company_id: Uuid.optional(),
});

export const OperatorBaselineView = z
  .object({
    scope: Scope,
    scope_id: Uuid.nullable(),
    generated_at: Timestamp,
    baseline: RenderedBaseline,
  })
  .meta({ id: "OperatorBaselineView" });

export const OperatorRosterQuery = z.object({ company_id: Uuid });

export const OperatorRosterDe = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    satellite_id: Uuid.nullable(),
    satellite_name: z.string().nullable(),
    container: z.string().nullable(),
    profile: z.string().nullable(),
    model: z.string().nullable(),
    runtime_profile: z.enum(["light", "standard", "heavy"]),
    /** A DE key exists for the profile (never the key). */
    has_key: z.boolean(),
    /** Fields an override changed. */
    overridden: z.array(z.enum(["placement", "model", "runtime_profile"])),
  })
  .meta({ id: "OperatorRosterDe" });

export const OperatorRosterView = z
  .object({
    company_id: Uuid,
    generated_at: Timestamp,
    des: z.array(OperatorRosterDe),
    /** The overrides version in force (null: none). */
    overrides_version: z.int().nullable(),
  })
  .meta({ id: "OperatorRosterView" });
export type OperatorRosterView = z.infer<typeof OperatorRosterView>;

// Margin ------------------------------------------------------------------------------------------

export const OperatorModelMarginQuery = z.object({
  month: z
    .string()
    .max(7)
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  company_id: Uuid.optional(),
});

const Money = {
  cost_micros: z.int(),
  price_micros: z.int(),
  margin_micros: z.int(),
};

export const OperatorModelMarginView = z
  .object({
    month: z.string(),
    rows: z.array(
      z.object({
        company_id: Uuid,
        company_name: z.string(),
        model: z.string().nullable(),
        runs: z.int().min(0),
        ...Money,
      }),
    ),
    by_model: z.array(z.object({ model: z.string().nullable(), runs: z.int().min(0), ...Money })),
    totals: z.object({ runs: z.int().min(0), ...Money }),
  })
  .meta({ id: "OperatorModelMarginView" });
export type OperatorModelMarginView = z.infer<typeof OperatorModelMarginView>;

// Operator agents ----------------------------------------------------------------------------------

/** The named operator APIs an agent or a job token may be allowed. */
export const OPERATOR_APIS = [
  "companies.from_pack",
  "baseline.apply",
  "jobs.read",
  "jobs.report",
] as const;
export const OperatorApi = z.enum(OPERATOR_APIS);
export type OperatorApi = z.infer<typeof OperatorApi>;

/** `gateway:<alias>` · `anthropic_api:<label>` · `subscription:<runner>`. */
export const OperatorModelSetting = z
  .string()
  .max(140)
  .regex(/^(gateway|anthropic_api|subscription):[A-Za-z0-9._/:@-]{1,120}$/);

export const OperatorJobTokenView = z
  .object({
    id: Uuid,
    prefix: z.string(),
    label: z.string(),
    apis: z.array(OperatorApi),
    expires_at: Timestamp,
    revoked_at: Timestamp.nullable(),
    last_used_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "OperatorJobTokenView" });
export type OperatorJobTokenView = z.infer<typeof OperatorJobTokenView>;

export const OperatorAgentView = z
  .object({
    id: Uuid,
    slug: OperatorAgentSlug,
    name: z.string(),
    purpose: z.string(),
    /** Always `system`: never DE or AG, never on a chart. */
    label: z.literal("system"),
    model_setting: z.string(),
    model_kind: z.enum(["gateway", "anthropic_api", "subscription"]),
    /** An Anthropic API key is sealed for it (never the key). */
    has_key: z.boolean(),
    key_hint: z.string().nullable(),
    allowed_apis: z.array(OperatorApi),
    daily_cap_micros: z.int().min(0),
    spent_today_micros: z.int().min(0),
    status: OperatorAgentStatus,
    tokens: z.array(OperatorJobTokenView),
    updated_at: Timestamp,
  })
  .meta({ id: "OperatorAgentView" });
export type OperatorAgentView = z.infer<typeof OperatorAgentView>;

export const OperatorAgentList = z
  .object({ agents: z.array(OperatorAgentView), apis: z.array(OperatorApi) })
  .meta({ id: "OperatorAgentList" });

export const OperatorAgentParams = z.object({ slug: OperatorAgentSlug });

export const OperatorAgentPut = z
  .strictObject({
    model_setting: OperatorModelSetting.optional(),
    allowed_apis: z.array(OperatorApi).max(20).optional(),
    daily_cap_micros: z.int().min(0).max(1_000_000_000).optional(),
    status: OperatorAgentStatus.optional(),
  })
  .meta({ id: "OperatorAgentPut" });

/** Seal an Anthropic API key for the agent, or remove it (`null`). Never returned. */
export const OperatorAgentKeyPut = z
  .strictObject({ key: z.string().trim().min(20).max(400).nullable() })
  .meta({ id: "OperatorAgentKeyPut" });

export const OperatorJobTokenCreate = z
  .strictObject({
    label: z.string().trim().min(1).max(60),
    apis: z.array(OperatorApi).min(1).max(20),
    days: z.int().min(1).max(90),
  })
  .meta({ id: "OperatorJobTokenCreate" });

export const OperatorJobTokenCreated = z
  .object({
    /** `cosop_…`, shown once. */
    token: z.string(),
    agent: OperatorAgentView,
  })
  .meta({ id: "OperatorJobTokenCreated" });

export const OperatorJobTokenParams = z.object({ slug: OperatorAgentSlug, id: Uuid });

// Jobs ----------------------------------------------------------------------------------------------

export const OperatorJobStep = z
  .object({
    step: z.string(),
    state: z.enum(["pending", "done", "skipped", "failed"]),
    detail: z.string(),
    at: Timestamp.nullable(),
  })
  .meta({ id: "OperatorJobStep" });

export const OperatorAgentJobView = z
  .object({
    id: Uuid,
    kind: z.enum(["company_from_pack", "apply_baseline"]),
    state: z.enum(["queued", "running", "done", "failed"]),
    agent: OperatorAgentSlug,
    company_id: Uuid.nullable(),
    /** What it was asked (never a secret). */
    payload: z.record(z.string(), z.unknown()),
    steps: z.array(OperatorJobStep),
    result: z.record(z.string(), z.unknown()).nullable(),
    last_error: z.string().nullable(),
    attempts: z.int().min(0),
    requested_by_user_id: Uuid.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
    finished_at: Timestamp.nullable(),
  })
  .meta({ id: "OperatorAgentJobView" });
export type OperatorAgentJobView = z.infer<typeof OperatorAgentJobView>;

export const OperatorJobList = z
  .object({ jobs: z.array(OperatorAgentJobView) })
  .meta({ id: "OperatorJobList" });

export const OperatorJobParams = z.object({ id: Uuid });
export const OperatorJobsQuery = z.object({
  state: z.enum(["queued", "running", "done", "failed"]).optional(),
});

/**
 * A company pack, v1 (inline until `wp/COMPANY-PACK` stores `cp.company_pack` with the chart, the
 * roles at template versions and the plan): the company, its settings overrides, where it runs.
 */
export const CompanyPackV1 = z
  .strictObject({
    name: NonEmpty,
    slug: Slug,
    reporting_currency: Currency,
    timezone: z.string().min(1).max(64).optional(),
    owner: z.strictObject({ email: Email, display_name: NonEmpty }).optional(),
    /** Company-scope settings (validated per key against its shape). */
    settings: boundedRecord(BoundedJson, 40, z.string().max(60)).optional(),
    /** The satellite its DEs run on (null: decided later). */
    satellite_id: Uuid.nullable().optional(),
  })
  .meta({ id: "CompanyPackV1" });
export type CompanyPackV1 = z.infer<typeof CompanyPackV1>;

export const CompanyFromPackRequest = z
  .strictObject({ pack: CompanyPackV1 })
  .meta({ id: "CompanyFromPackRequest" });

export const ApplyBaselineRequest = z
  .strictObject({
    satellite_id: Uuid,
    /** One container; absent: every container on the satellite. */
    container: z.string().trim().min(1).max(128).nullable().optional(),
    /** Preview only: ClawPanel reports the drift, nothing changes. */
    dry_run: z.boolean(),
  })
  .meta({ id: "ApplyBaselineRequest" });

// The runner (job token) ------------------------------------------------------------------------------

export const OperatorRunnerClaim = z
  .strictObject({
    kinds: z
      .array(z.enum(["company_from_pack", "apply_baseline"]))
      .min(1)
      .max(5),
  })
  .meta({ id: "OperatorRunnerClaim" });

export const OperatorRunnerClaimed = z
  .object({
    job: OperatorAgentJobView.nullable(),
    /** The claim's fence: every report on the job sends it back (null: nothing claimed). */
    lease: Uuid.nullable(),
  })
  .meta({ id: "OperatorRunnerClaimed" });

export const OperatorRunnerReport = z
  .strictObject({
    /** The `lease` the claim returned; a lapsed or retaken claim is refused (409). */
    lease: Uuid,
    step: z.string().trim().min(1).max(60),
    state: z.enum(["done", "skipped", "failed"]),
    detail: z.string().trim().max(500),
    /** Set to finish the job. */
    outcome: z.enum(["done", "failed"]).optional(),
    result: BoundedJson.optional(),
  })
  .meta({ id: "OperatorRunnerReport" });

// Routes ----------------------------------------------------------------------------------------------

const errorsRead = ["forbidden", "not_found"] as const;
const errorsWrite = ["forbidden", "not_found", "conflict"] as const;

export const operatorConsoleRoutes = {
  "operator.settings.list": defineRoute({
    ...operator,
    id: "operator.settings.list",
    method: "get",
    path: `${op}/settings`,
    summary: "Every operator setting: its scopes, the platform version in force, overrides",
    response: { status: 200, schema: OperatorSettingList },
    errors: [...errorsRead],
    emits: [],
  }),
  "operator.settings.get": defineRoute({
    ...operator,
    id: "operator.settings.get",
    method: "get",
    path: `${op}/settings/{key}`,
    summary: "One operator setting at a scope: default, platform, override, what applies, history",
    params: OperatorSettingParams,
    query: OperatorSettingQuery,
    response: { status: 200, schema: OperatorSettingDetail },
    errors: [...errorsRead],
    emits: [],
  }),
  "operator.settings.put": defineRoute({
    ...operator,
    id: "operator.settings.put",
    method: "put",
    path: `${op}/settings/{key}`,
    summary: "Apply a new version of an operator setting (or clear an override); audited",
    params: OperatorSettingParams,
    body: OperatorSettingPut,
    // A control-plane store and its `platform_audit` row; no company state, so no company event.
    safe: true,
    response: { status: 200, schema: OperatorSettingDetail },
    errors: [...errorsWrite],
    emits: [],
  }),
  "operator.settings.rollback": defineRoute({
    ...operator,
    id: "operator.settings.rollback",
    method: "post",
    path: `${op}/settings/{key}/rollback`,
    summary: "Move an operator setting's pointer back to an earlier version; audited",
    params: OperatorSettingParams,
    body: OperatorSettingRollback,
    safe: true,
    response: { status: 200, schema: OperatorSettingDetail },
    errors: [...errorsWrite],
    emits: [],
  }),
  "operator.baseline.get": defineRoute({
    ...operator,
    id: "operator.baseline.get",
    method: "get",
    path: `${op}/baseline`,
    summary:
      "The ClawPanel baseline rendered from the settings (platform, a company or a satellite)",
    query: OperatorBaselineQuery,
    response: { status: 200, schema: OperatorBaselineView },
    errors: [...errorsRead],
    emits: [],
  }),
  "operator.roster.get": defineRoute({
    ...operator,
    id: "operator.roster.get",
    method: "get",
    path: `${op}/roster`,
    summary: "A company's roster, generated from the registry and memberships, with its overrides",
    query: OperatorRosterQuery,
    response: { status: 200, schema: OperatorRosterView },
    errors: [...errorsRead],
    emits: [],
  }),
  "operator.margin.models": defineRoute({
    ...operator,
    id: "operator.margin.models",
    method: "get",
    path: `${op}/margin/models`,
    summary: "A month's cost, client price and margin per company and model, from the runs",
    query: OperatorModelMarginQuery,
    response: { status: 200, schema: OperatorModelMarginView },
    errors: [...errorsRead],
    emits: [],
  }),
  "operator.agents.list": defineRoute({
    ...operator,
    id: "operator.agents.list",
    method: "get",
    path: `${op}/agents`,
    summary: "The operator agents (Deployer, Fixer, Builder): model setting, APIs, cap, tokens",
    response: { status: 200, schema: OperatorAgentList },
    errors: [...errorsRead],
    emits: [],
  }),
  "operator.agents.put": defineRoute({
    ...operator,
    id: "operator.agents.put",
    method: "put",
    path: `${op}/agents/{slug}`,
    summary: "Change an operator agent's model setting, allowed APIs, daily cap or status; audited",
    params: OperatorAgentParams,
    body: OperatorAgentPut,
    safe: true,
    response: { status: 200, schema: OperatorAgentView },
    errors: [...errorsWrite],
    emits: [],
  }),
  "operator.agents.key.put": defineRoute({
    ...operator,
    id: "operator.agents.key.put",
    method: "put",
    path: `${op}/agents/{slug}/key`,
    summary: "Seal an operator agent's Anthropic API key in the keyring, or remove it; audited",
    params: OperatorAgentParams,
    body: OperatorAgentKeyPut,
    safe: true,
    response: { status: 200, schema: OperatorAgentView },
    errors: [...errorsWrite],
    emits: [],
  }),
  "operator.agents.tokens.create": defineRoute({
    ...operator,
    id: "operator.agents.tokens.create",
    method: "post",
    path: `${op}/agents/{slug}/tokens`,
    summary: "Issue a job token scoped to named operator APIs (shown once); audited",
    params: OperatorAgentParams,
    body: OperatorJobTokenCreate,
    safe: true,
    response: { status: 201, schema: OperatorJobTokenCreated },
    errors: [...errorsWrite],
    emits: [],
  }),
  "operator.agents.tokens.revoke": defineRoute({
    ...operator,
    id: "operator.agents.tokens.revoke",
    method: "post",
    path: `${op}/agents/{slug}/tokens/{id}/revoke`,
    summary: "Revoke a job token; audited",
    params: OperatorJobTokenParams,
    safe: true,
    response: { status: 200, schema: OperatorAgentView },
    errors: [...errorsWrite],
    emits: [],
  }),
  "operator.jobs.list": defineRoute({
    ...operator,
    id: "operator.jobs.list",
    method: "get",
    path: `${op}/jobs`,
    summary: "The operator agents' jobs, newest first (at most 100)",
    query: OperatorJobsQuery,
    response: { status: 200, schema: OperatorJobList },
    errors: [...errorsRead],
    emits: [],
  }),
  "operator.jobs.get": defineRoute({
    ...operator,
    id: "operator.jobs.get",
    method: "get",
    path: `${op}/jobs/{id}`,
    summary: "One operator agent job and its checklist",
    params: OperatorJobParams,
    response: { status: 200, schema: OperatorAgentJobView },
    errors: [...errorsRead],
    emits: [],
  }),
  "operator.jobs.retry": defineRoute({
    ...operator,
    id: "operator.jobs.retry",
    method: "post",
    path: `${op}/jobs/{id}/retry`,
    summary: "Queue a failed job again; it resumes at the step that failed",
    params: OperatorJobParams,
    safe: true,
    response: { status: 200, schema: OperatorAgentJobView },
    errors: [...errorsWrite],
    emits: [],
  }),
  "operator.deployer.companyFromPack": defineRoute({
    ...operator,
    id: "operator.deployer.companyFromPack",
    method: "post",
    path: `${op}/deployer/company-from-pack`,
    summary:
      "Queue the Deployer's New company from pack: a resumable checklist to a green health check",
    body: CompanyFromPackRequest,
    safe: true,
    response: { status: 201, schema: OperatorAgentJobView },
    errors: [...errorsWrite],
    emits: [],
  }),
  "operator.deployer.applyBaseline": defineRoute({
    ...operator,
    id: "operator.deployer.applyBaseline",
    method: "post",
    path: `${op}/deployer/apply-baseline`,
    summary: "Queue the Deployer's Apply baseline for a satellite (or one container), or a dry run",
    body: ApplyBaselineRequest,
    safe: true,
    response: { status: 201, schema: OperatorAgentJobView },
    errors: [...errorsWrite],
    emits: [],
  }),
  "operatorRunner.claim": defineRoute({
    ...runner,
    id: "operatorRunner.claim",
    method: "post",
    path: "/v1/operator-runner/claim",
    summary: "An operator runner takes the next queued job its token allows (job token)",
    body: OperatorRunnerClaim,
    safe: true,
    response: { status: 200, schema: OperatorRunnerClaimed },
    errors: ["unauthenticated", "forbidden"],
    emits: [],
  }),
  "operatorRunner.report": defineRoute({
    ...runner,
    id: "operatorRunner.report",
    method: "post",
    path: "/v1/operator-runner/jobs/{id}/report",
    summary: "An operator runner reports a step of its job, or finishes it (job token)",
    params: OperatorJobParams,
    body: OperatorRunnerReport,
    safe: true,
    response: { status: 200, schema: OperatorAgentJobView },
    errors: ["unauthenticated", "forbidden", "not_found", "conflict"],
    emits: [],
  }),
} as const;
